/**
 * Frame-coalesced text buffer for streamed model output (ported from Flint's readTurnStream):
 * many tiny deltas per frame become one registry update per animation frame, so the panel
 * re-renders at most ~60 times a second while tokens arrive. Non-text events must call
 * `flush()` first so the order of text and tool events is preserved.
 */

/**
 * Schedule a callback on the next animation frame (setTimeout fallback outside browsers).
 * @param {() => void} callback - Callback.
 * @returns {{cancel: () => void}} Handle.
 */
const scheduleFrame = (callback) => {
  if (typeof requestAnimationFrame === "function") {
    const id = requestAnimationFrame(callback);
    return { cancel: () => cancelAnimationFrame(id) };
  }
  const id = setTimeout(callback, 16);
  return { cancel: () => clearTimeout(id) };
};

/**
 * Create a frame-coalesced text buffer.
 * @param {(delta: string) => void} onFlush - Receives the coalesced delta.
 * @returns {{push: (delta: string) => void, flush: () => void, reset: () => void, cancel: () => void}} Buffer API.
 */
export const createFrameTextBuffer = (onFlush) => {
  let pending = "";
  let scheduled = null;

  const flush = () => {
    scheduled?.cancel();
    scheduled = null;
    if (!pending) return;
    const delta = pending;
    pending = "";
    onFlush(delta);
  };

  return {
    push(delta) {
      if (!delta) return;
      pending += delta;
      if (scheduled) return;
      scheduled = scheduleFrame(() => {
        scheduled = null;
        flush();
      });
    },
    flush,
    reset() {
      scheduled?.cancel();
      scheduled = null;
      pending = "";
    },
    cancel() {
      scheduled?.cancel();
      scheduled = null;
    },
  };
};

/**
 * Incrementally read the value of a string field out of a partial JSON document, e.g. the
 * `content` of `{"type":"message","content":"Hel` -> `Hel`. Used by the legacy text-JSON
 * protocol so the bubble streams prose instead of raw JSON. Incomplete escape sequences at
 * the end of the buffer are held back until the next delta completes them.
 * @param {string} buffer - Partial JSON text.
 * @param {string} field - Field name to read.
 * @returns {string|null} Decoded (possibly partial) value, or null when the field has not started.
 */
export const readPartialJsonString = (buffer, field) => {
  const text = String(buffer || "");
  const pattern = new RegExp(`"${field}"\\s*:\\s*"`);
  const match = pattern.exec(text);
  if (!match) return null;
  let out = "";
  for (let i = match.index + match[0].length; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') return out;
    if (ch !== "\\") {
      out += ch;
      continue;
    }
    const next = text[i + 1];
    if (next === undefined) return out;
    if (next === "u") {
      const hex = text.slice(i + 2, i + 6);
      if (hex.length < 4 || !/^[0-9a-fA-F]{4}$/.test(hex)) return out;
      out += String.fromCharCode(parseInt(hex, 16));
      i += 5;
      continue;
    }
    const map = { n: "\n", t: "\t", r: "\r", b: "\b", f: "\f", '"': '"', "\\": "\\", "/": "/" };
    out += map[next] ?? next;
    i += 1;
  }
  return out;
};
